import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { usersApi } from '@/lib/api'
import type { SystemUser, UserFormData } from '@/types'

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: () => [...userKeys.lists()] as const,
}

export function useUsers(): UseQueryResult<SystemUser[]> {
  return useQuery({
    queryKey: userKeys.list(),
    queryFn: async () => {
      const res = await usersApi.list()
      return res.data
    },
  })
}

export function useCreateUser(): UseMutationResult<SystemUser, Error, UserFormData> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UserFormData) => {
      const res = await usersApi.create(data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

export function useUpdateUser(): UseMutationResult<
  SystemUser,
  Error,
  { id: number; data: UserFormData }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await usersApi.update(id, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

export function useDeleteUser(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await usersApi.delete(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}
